import { useState } from "react"
import { format } from "date-fns"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Button } from "@/modules/shared/ui/components/button"
import { Skeleton } from "@/modules/shared/ui/components/skeleton"
import { useResourceAssignmentAuditLog } from "../../hooks/useResourceAssignmentAuditLog"
import { ResourceAssignmentAuditDto, ResourceType } from "../../dtos/ResourceAccessDtos"

interface AssignmentAuditLogProps {
  type: ResourceType
  resourceId: string
  /** userId -> display name, built from the current assignments list — falls back to a short id
   * for a revoked user who no longer has a row there. */
  userNames: Map<string, string>
}

function describeAction(entry: ResourceAssignmentAuditDto, userNames: Map<string, string>) {
  const assignee =
    entry.userName ?? userNames.get(entry.userId) ?? `User ${entry.userId.slice(0, 8)}`

  switch (entry.action) {
    case "Granted":
      return `${assignee} was granted ${entry.newLevel} access`
    case "Revoked":
      return `${assignee}'s access was removed`
    case "LevelChanged":
      return `${assignee}'s access changed from ${entry.oldLevel} to ${entry.newLevel}`
    default:
      return `${assignee}'s access changed`
  }
}

export function AssignmentAuditLog({ type, resourceId, userNames }: AssignmentAuditLogProps) {
  const [page, setPage] = useState(1)
  const { data, isLoading } = useResourceAssignmentAuditLog(type, resourceId, page)

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-10 w-full" />
        ))}
      </div>
    )
  }

  const items = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const hasMore = page * (data?.pageSize ?? 20) < totalCount

  if (items.length === 0 && page === 1) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        No access changes have been made to this record yet.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Change</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">By</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">When</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((entry) => (
              <tr key={entry.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-3 py-2">
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="shrink-0">{entry.action}</Badge>
                    <span>{describeAction(entry, userNames)}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {entry.performedByName ?? "—"}
                </td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                  {format(new Date(entry.performedAt), "MMM d, yyyy HH:mm")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(page > 1 || hasMore) && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasMore}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
